import type { GeneratedDocumentsDisplayConfig } from 'korint-sdk/react';

export const DOCUMENT_GROUPS = ['policy', 'terms', 'information', 'notices'] as const;

type GroupableDocument = {
  id: string;
  category?: string | null;
  type?: string | null;
};

export type DocumentGroup<TDocument> = {
  id: string;
  documents: TDocument[];
};

export function getDocumentGroup(
  category: string,
  commonDocumentTypes: readonly string[],
): (typeof DOCUMENT_GROUPS)[number] {
  if (!commonDocumentTypes.includes(category)) return 'policy';

  const tokens = category.split('_');
  if (tokens.includes('IPID') || category.includes('INSURANCE_PRODUCT_INFORMATION_DOCUMENT')) {
    return 'information';
  }
  if (
    tokens.includes('TERMS') ||
    tokens.includes('AGREEMENT') ||
    ['CONDITIONS_GENERALES', 'GUARANTEES_TABLE', 'BAREME'].includes(category)
  ) {
    return 'terms';
  }
  return 'notices';
}

export function getDocumentGroups<TDocument extends GroupableDocument>({
  documents,
  commonDocumentTypes,
  generatedDocumentsDisplay,
}: {
  documents: readonly TDocument[];
  commonDocumentTypes: readonly string[];
  generatedDocumentsDisplay?: GeneratedDocumentsDisplayConfig;
}): DocumentGroup<TDocument>[] {
  const groupedDocumentIds = new Set<string>();
  const configuredGroups = (generatedDocumentsDisplay?.groups ?? []).map(group => ({
    id: group.id,
    documents: group.categories.flatMap(category =>
      documents.filter(document => {
        if (groupedDocumentIds.has(document.id)) return false;
        if (document.type !== 'generated' || document.category !== category) return false;
        groupedDocumentIds.add(document.id);
        return true;
      }),
    ),
  }));
  const fallbackGroups = DOCUMENT_GROUPS.map(group => ({
    id: group,
    documents: documents.filter(document => {
      if (groupedDocumentIds.has(document.id) || !document.category) return false;
      if (getDocumentGroup(document.category, commonDocumentTypes) !== group) return false;
      groupedDocumentIds.add(document.id);
      return true;
    }),
  }));

  return [...configuredGroups, ...fallbackGroups].filter(group => group.documents.length > 0);
}
